import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { Castle, Hotel, Rocket, CheckCircle, Trophy, Star, Ticket, Users, Music, LogOut, Moon, Sun, Search, X, StickyNote, Clock, Camera, Timer, ShoppingBag, Plus, Minus } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FamilyProvider, useFamily } from './contexts/FamilyContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { WaitTimesProvider, useWaitTimes } from './contexts/WaitTimesContext';
import { NotificationAlertsProvider } from './contexts/NotificationAlertsContext';
import { SentryErrorBoundary, setSentryUser } from './sentry';
import { identifyUser, clearUserIdentity, trackTabView, trackItemChecked, trackWishlistToggle, trackPhotoUpload, trackNoteAdded, trackSearch, trackDarkModeToggle, trackVisitIncremented, trackVisitDecremented } from './analytics';
import { LandingPage } from './components/LandingPage';
import FamilyManager from './components/FamilyManager';
import ProfileSelector from './components/ProfileSelector';
const Leaderboard = lazy(() => import('./components/Leaderboard'));
const WaitTimes = lazy(() => import('./components/WaitTimes'));
const BookTrip = lazy(() => import('./components/BookTrip'));
import { PARKS_DATA, ENTERTAINMENT_DATA, HOTELS_DATA } from './data';

// --- Static searchable items index ---
const ALL_SEARCHABLE_ITEMS = (() => {
  const items = [];
  PARKS_DATA.disneylandPark.lands.forEach(land => {
    land.attractions.forEach(name => {
      items.push({ name, category: 'Disneyland Park', subcategory: land.name, colorClass: 'text-pink-500' });
    });
  });
  PARKS_DATA.wds.lands.forEach(land => {
    land.attractions.forEach(name => {
      items.push({ name, category: 'Disney Adventure World', subcategory: land.name, colorClass: 'text-blue-500' });
    });
  });
  ENTERTAINMENT_DATA.disneylandPark.sections.forEach(section => {
    section.items.forEach(name => {
      items.push({ name, category: 'Disneyland Park', subcategory: section.title, colorClass: 'text-purple-500' });
    });
  });
  ENTERTAINMENT_DATA.wds.sections.forEach(section => {
    section.items.forEach(name => {
      items.push({ name, category: 'Disney Adventure World', subcategory: section.title, colorClass: 'text-indigo-500' });
    });
  });
  HOTELS_DATA.forEach(hotel => {
    items.push({ name: hotel.name, category: 'Hotels', subcategory: `${hotel.stars > 0 ? hotel.stars + '-star' : 'Resort'} \u2022 ${hotel.theme}`, colorClass: 'text-amber-500' });
  });
  return items;
})();

// --- Reusable Components ---

const TabLoadingFallback = () => (
  <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
    <div className="h-8 w-8 border-4 border-gray-200 dark:border-gray-700 border-t-indigo-500 rounded-full animate-spin mb-4" />
    <p className="text-sm font-medium">Loading...</p>
  </div>
);

const ProgressBar = ({ current, total, colorClass = "bg-blue-500" }) => {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div className="w-full mt-2">
      <div className="flex justify-between text-xs mb-1 text-gray-600 dark:text-gray-400 font-medium">
        <span>Progress</span>
        <span>{percentage}% ({current}/{total})</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all duration-500 ${colorClass}`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex flex-col items-center justify-center py-3 px-1 text-xs sm:text-sm font-medium transition-colors border-b-4 ${active
      ? 'border-indigo-600 text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
      }`}
  >
    <div className="mb-1">{icon}</div>
    <span className="text-center leading-tight">{label}</span>
  </button>
);

// Inline wait time badge for ride items
const InlineWaitBadge = ({ waitData }) => {
  if (!waitData) return null;
  const status = waitData.status;
  const standby = waitData.queue?.STANDBY?.waitTime;

  if (status === 'DOWN') {
    return <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400">Down</span>;
  }
  if (status === 'CLOSED') {
    return <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">Closed</span>;
  }
  if (status === 'REFURBISHMENT') {
    return <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400">Refurb</span>;
  }
  if (status === 'OPERATING') {
    if (standby != null && standby > 0) {
      let colorClasses;
      if (standby <= 15) colorClasses = 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
      else if (standby <= 30) colorClasses = 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
      else if (standby <= 60) colorClasses = 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
      else colorClasses = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
      return (
        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${colorClasses}`}>
          <Timer size={10} />{standby}m
        </span>
      );
    }
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400">Walk On</span>;
  }
  return null;
};

const CheckboxItem = ({ label, subtitle, checked, visitCount = 0, onChange, onIncrement, onDecrement, colorClass = "text-gray-400", wishlisted, onToggleWishlist, note, onUpdateNote, waitData, photoUrl, onPhotoUpload }) => {
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const prevChecked = useRef(checked);

  // Detect when item is newly checked → show inline photo upload
  useEffect(() => {
    if (checked && !prevChecked.current && onPhotoUpload) {
      setShowPhotoUpload(true);
      setPhotoFile(null);
    }
    prevChecked.current = checked;
  }, [checked, onPhotoUpload]);

  const handleOpenNote = () => {
    if (showNoteInput) {
      setShowNoteInput(false);
    } else {
      setNoteText(note || '');
      setShowNoteInput(true);
    }
  };

  const handleSaveNote = () => {
    onUpdateNote(noteText.trim());
    if (noteText.trim()) trackNoteAdded(label);
    setShowNoteInput(false);
  };

  const handleNoteKeyDown = (e) => {
    if (e.key === 'Enter') handleSaveNote();
    if (e.key === 'Escape') setShowNoteInput(false);
  };

  const handlePhotoUpload = async () => {
    if (photoFile && onPhotoUpload) {
      setUploading(true);
      try {
        await onPhotoUpload(photoFile);
      } finally {
        setUploading(false);
        setShowPhotoUpload(false);
        setPhotoFile(null);
      }
    }
  };

  return (
    <div className="border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="flex items-center p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
        {/* Wishlist star */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleWishlist(); }}
          className={`mr-2 flex-shrink-0 transition-colors ${wishlisted ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600 hover:text-yellow-300'}`}
          title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <Star size={16} fill={wishlisted ? 'currentColor' : 'none'} />
        </button>

        {/* Checkbox + Label + Wait badge */}
        <label className="flex items-start cursor-pointer flex-1 min-w-0">
          <div className="relative flex items-center mt-0.5">
            <input
              type="checkbox"
              className={`peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 dark:border-gray-600 shadow-sm checked:bg-current focus:ring-2 focus:ring-offset-1 focus:outline-none transition-all ${colorClass}`}
              checked={!!checked}
              onChange={onChange}
            />
            <CheckCircle className="pointer-events-none absolute h-5 w-5 text-white opacity-0 peer-checked:opacity-100 transition-opacity" size={14} />
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-sm sm:text-base select-none transition-colors ${checked ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-700 dark:text-gray-200'}`}>{label}</span>
              <InlineWaitBadge waitData={waitData} />
            </div>
            {subtitle && <div className="mt-0.5">{subtitle}</div>}
          </div>
        </label>

        {/* Visit counter + stepper (visible when count >= 1) */}
        {visitCount >= 1 && (
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded-full min-w-[1.5rem] text-center">
              ×{visitCount}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); onDecrement?.(); }}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title="Remove one visit"
            >
              <Minus size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onIncrement?.(); }}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-green-100 dark:hover:bg-green-900/30 hover:text-green-600 dark:hover:text-green-400 transition-colors"
              title="Add another visit"
            >
              <Plus size={14} />
            </button>
          </div>
        )}

        {/* Photo button (shows camera if photo exists or for re-upload) */}
        {onPhotoUpload && checked && (
          <button
            onClick={() => { setShowPhotoUpload(!showPhotoUpload); setPhotoFile(null); }}
            className={`ml-1.5 flex-shrink-0 transition-colors ${photoUrl ? 'text-green-500 dark:text-green-400' : 'text-gray-300 dark:text-gray-600 hover:text-green-400'}`}
            title={photoUrl ? 'Change photo' : 'Add photo'}
          >
            <Camera size={16} />
          </button>
        )}

        {/* Note button */}
        <button
          onClick={handleOpenNote}
          className={`ml-1.5 flex-shrink-0 transition-colors ${note ? 'text-blue-500 dark:text-blue-400' : 'text-gray-300 dark:text-gray-600 hover:text-blue-400'}`}
          title={note ? 'Edit note' : 'Add note'}
        >
          <StickyNote size={16} />
        </button>
      </div>

      {/* Photo thumbnail (when photo exists and not uploading) */}
      {photoUrl && !showPhotoUpload && (
        <div className="px-4 pb-3 pl-14">
          <img src={photoUrl} alt={label} className="h-16 w-16 object-cover rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm" />
        </div>
      )}

      {/* Inline photo upload */}
      {showPhotoUpload && (
        <div className="px-4 pb-3 pl-14">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium flex items-center gap-1">
              <Camera size={12} /> {photoUrl ? 'Replace photo' : 'Capture the moment!'}
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setPhotoFile(e.target.files[0] || null)}
              disabled={uploading}
              className="text-xs text-gray-600 dark:text-gray-300 mb-2 w-full file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-indigo-50 file:text-indigo-600 dark:file:bg-indigo-900/40 dark:file:text-indigo-400 hover:file:bg-indigo-100 dark:hover:file:bg-indigo-900/60"
            />
            <div className="flex gap-2">
              <button
                onClick={handlePhotoUpload}
                disabled={!photoFile || uploading}
                className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                onClick={() => { setShowPhotoUpload(false); setPhotoFile(null); }}
                disabled={uploading}
                className="text-xs px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Display existing note (when not editing) */}
      {note && !showNoteInput && (
        <div className="px-4 pb-3 pl-14">
          <p className="text-xs text-gray-500 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-700/50 px-3 py-1.5 rounded">{note}</p>
        </div>
      )}

      {/* Note input */}
      {showNoteInput && (
        <div className="px-4 pb-3 pl-14">
          <div className="flex gap-2">
            <input
              type="text"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={handleNoteKeyDown}
              placeholder="Add a note..."
              className="flex-1 text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 placeholder-gray-400 dark:placeholder-gray-500"
              autoFocus
            />
            <button onClick={handleSaveNote} className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">Save</button>
            <button onClick={() => setShowNoteInput(false)} className="text-xs px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Main App ---

function TrackerApp() {
  const [activeTab, setActiveTab] = useState('park1');
  const { currentUser, logout } = useAuth();
  const { updateProgress, getRideStatus, getVisitCount, updateVisitCount, addPhoto, getPhotoUrl, toggleWishlist, getWishlistStatus, updateNote, getNote } = useFamily();
  const { darkMode, toggleDarkMode } = useTheme();
  const { getWaitData } = useWaitTimes();

  // New state for search & wishlist filter
  const [searchQuery, setSearchQuery] = useState('');
  const [showWishlistOnly, setShowWishlistOnly] = useState(false);

  // All hooks MUST be above the early return to avoid "Rendered more hooks" error
  const searchTimerRef = useRef(null);

  // Set Sentry + Analytics user context
  useEffect(() => {
    setSentryUser(currentUser);
    if (currentUser) {
      identifyUser(currentUser);
    } else {
      clearUserIdentity();
    }
  }, [currentUser]);

  // Track tab views
  useEffect(() => {
    trackTabView(activeTab);
  }, [activeTab]);

  // Track search queries (debounced) — hook must stay above early return
  const isSearchActive = searchQuery.trim().length > 0;
  useEffect(() => {
    if (!isSearchActive) return;
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      trackSearch(searchQuery.trim(), 0); // result count filled lazily
    }, 800);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery, isSearchActive]);

  if (!currentUser) {
    return <LandingPage />;
  }

  const toggleItem = (itemName) => {
    const currentStatus = getRideStatus(itemName);
    updateProgress(itemName, !currentStatus);
    trackItemChecked(itemName, !currentStatus);
  };

  const incrementVisit = (itemName) => {
    const currentCount = getVisitCount(itemName);
    updateVisitCount(itemName, currentCount + 1);
    trackVisitIncremented(itemName, currentCount + 1);
  };

  const decrementVisit = (itemName) => {
    const currentCount = getVisitCount(itemName);
    if (currentCount <= 1) {
      updateProgress(itemName, false);
      trackItemChecked(itemName, false);
    } else {
      updateVisitCount(itemName, currentCount - 1);
      trackVisitDecremented(itemName, currentCount - 1);
    }
  };

  const getProgress = (items) => {
    let completed = 0;
    let totalVisits = 0;
    items.forEach(item => {
      const count = getVisitCount(item);
      if (count > 0) completed++;
      totalVisits += count;
    });
    return { completed, total: items.length, totalVisits };
  };

  // Filter helper for wishlist mode
  const shouldShowItem = (itemName) => {
    if (!showWishlistOnly) return true;
    return getWishlistStatus(itemName);
  };

  // --- Aggregation Logic ---
  const park1Rides = PARKS_DATA.disneylandPark.lands.flatMap(l => l.attractions);
  const park2Rides = PARKS_DATA.wds.lands.flatMap(l => l.attractions);
  const park1Ent = ENTERTAINMENT_DATA.disneylandPark.sections.flatMap(s => s.items);
  const park2Ent = ENTERTAINMENT_DATA.wds.sections.flatMap(s => s.items);
  const allEnt = [...park1Ent, ...park2Ent];
  const allHotels = HOTELS_DATA.map(h => h.name);

  const p1Stats = getProgress(park1Rides);
  const p2Stats = getProgress(park2Rides);
  const entStats = getProgress(allEnt);
  const hotelStats = getProgress(allHotels);

  const totalRidesCompleted = p1Stats.completed + p2Stats.completed;
  const totalRides = p1Stats.total + p2Stats.total;
  const totalRideVisits = p1Stats.totalVisits + p2Stats.totalVisits;

  // --- Search Logic ---
  const isSearching = searchQuery.trim().length > 0;
  const searchResults = isSearching
    ? ALL_SEARCHABLE_ITEMS.filter(item => {
        const matchesQuery = item.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesWishlist = !showWishlistOnly || getWishlistStatus(item.name);
        return matchesQuery && matchesWishlist;
      })
    : [];

  // Group search results by category
  const groupedResults = {};
  searchResults.forEach(item => {
    if (!groupedResults[item.category]) groupedResults[item.category] = [];
    groupedResults[item.category].push(item);
  });

  // Helper to render a CheckboxItem with all feature props
  const renderItem = (name, colorClass, subtitle) => (
    <CheckboxItem
      key={name}
      label={name}
      subtitle={subtitle}
      checked={getRideStatus(name)}
      visitCount={getVisitCount(name)}
      onChange={() => toggleItem(name)}
      onIncrement={() => incrementVisit(name)}
      onDecrement={() => decrementVisit(name)}
      colorClass={colorClass}
      wishlisted={getWishlistStatus(name)}
      onToggleWishlist={() => { toggleWishlist(name); trackWishlistToggle(name, !getWishlistStatus(name)); }}
      note={getNote(name)}
      onUpdateNote={(text) => updateNote(name, text)}
      waitData={getWaitData(name)}
      photoUrl={getPhotoUrl(name)}
      onPhotoUpload={(file) => { trackPhotoUpload(name); return addPhoto(name, file); }}
    />
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 font-sans text-gray-900 dark:text-gray-100 pb-24 md:pb-10 transition-colors">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-800 via-purple-800 to-fuchsia-700 text-white p-6 shadow-lg">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Castle className="text-yellow-300" /> DLP Tracker
              </h1>
              <p className="text-indigo-200 text-sm mt-1">Rides, Shows & Magic</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block mr-4">
                <div className="text-2xl font-bold">{totalRidesCompleted} / {totalRides}</div>
                <div className="text-xs text-indigo-200 uppercase tracking-wide">Rides Done</div>
                {totalRideVisits > totalRidesCompleted && (
                  <div className="text-xs text-indigo-300 mt-0.5">{totalRideVisits} total visits</div>
                )}
              </div>
              {/* Dark mode toggle */}
              <button
                onClick={() => { toggleDarkMode(); trackDarkModeToggle(!darkMode); }}
                className="text-indigo-200 hover:text-white transition-colors p-1"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <button onClick={logout} className="text-indigo-200 hover:text-white transition-colors" title="Log Out">
                <LogOut size={20} />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 items-center justify-between bg-white/10 p-3 rounded-lg backdrop-blur-sm">
            <ProfileSelector />
            <FamilyManager />
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="sticky top-0 z-20 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 overflow-x-auto hide-scrollbar">
        <div className="max-w-3xl mx-auto flex min-w-full sm:min-w-0">
          <TabButton active={activeTab === 'live'} onClick={() => setActiveTab('live')} icon={<Clock size={18} />} label="Live" />
          <TabButton active={activeTab === 'park1'} onClick={() => setActiveTab('park1')} icon={<Castle size={18} />} label="Park" />
          <TabButton active={activeTab === 'park2'} onClick={() => setActiveTab('park2')} icon={<Rocket size={18} />} label="Adventure" />
          <TabButton active={activeTab === 'shows'} onClick={() => setActiveTab('shows')} icon={<Ticket size={18} />} label="Shows" />
          <TabButton active={activeTab === 'hotels'} onClick={() => setActiveTab('hotels')} icon={<Hotel size={18} />} label="Hotels" />
          <TabButton active={activeTab === 'book'} onClick={() => setActiveTab('book')} icon={<ShoppingBag size={18} />} label="Book" />
          <TabButton active={activeTab === 'stats'} onClick={() => setActiveTab('stats')} icon={<Trophy size={18} />} label="Stats" />
        </div>
      </div>

      <main className="max-w-3xl mx-auto p-4">

        {/* Search & Wishlist Filter Bar (hidden on stats, live, and book tabs) */}
        {activeTab !== 'stats' && activeTab !== 'live' && activeTab !== 'book' && (
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search rides, shows, hotels..."
                className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 placeholder-gray-400 dark:placeholder-gray-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowWishlistOnly(!showWishlistOnly)}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                showWishlistOnly
                  ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700'
                  : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              title={showWishlistOnly ? 'Show all items' : 'Show wishlist only'}
            >
              <Star size={16} fill={showWishlistOnly ? 'currentColor' : 'none'} />
              <span className="hidden sm:inline">Wishlist</span>
            </button>
          </div>
        )}

        {/* --- LIVE WAIT TIMES TAB --- */}
        {activeTab === 'live' && (
          <Suspense fallback={<TabLoadingFallback />}>
            <WaitTimes />
          </Suspense>
        )}

        {/* --- SEARCH RESULTS --- */}
        {isSearching && activeTab !== 'stats' && activeTab !== 'live' && activeTab !== 'book' ? (
          <div className="space-y-4 animate-in fade-in duration-300">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;
            </p>
            {searchResults.length === 0 ? (
              <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                No matching items found
              </div>
            ) : (
              Object.entries(groupedResults).map(([category, items]) => (
                <div key={category} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700">
                  <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/70 font-semibold text-gray-700 dark:text-gray-200 text-sm border-b border-gray-100 dark:border-gray-700">
                    {category}
                  </div>
                  {items.map(item =>
                    renderItem(
                      item.name,
                      item.colorClass,
                      <span className="text-xs text-gray-400 dark:text-gray-500">{item.subcategory}</span>
                    )
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          <>
            {/* --- DISNEYLAND PARK TAB --- */}
            {activeTab === 'park1' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-pink-100 dark:border-pink-900/40">
                  <h2 className="text-xl font-bold text-pink-600 dark:text-pink-400 mb-1">Disneyland Park Rides</h2>
                  <ProgressBar current={p1Stats.completed} total={p1Stats.total} colorClass="bg-pink-500" />
                </div>

                {PARKS_DATA.disneylandPark.lands.map((land) => {
                  const filteredAttractions = land.attractions.filter(shouldShowItem);
                  if (showWishlistOnly && filteredAttractions.length === 0) return null;
                  const landStats = getProgress(land.attractions);
                  const isFullyComplete = landStats.completed === landStats.total && landStats.total > 0;

                  return (
                    <div key={land.name} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border-l-4 ${isFullyComplete ? 'border-green-400' : 'border-pink-500'}`}>
                      <div className="bg-pink-50 dark:bg-pink-900/20 px-4 py-3 flex justify-between items-center border-b border-pink-100 dark:border-pink-900/40">
                        <h3 className="font-bold text-pink-900 dark:text-pink-300">{land.name}</h3>
                        <span className="text-xs font-mono bg-white dark:bg-gray-700 px-2 py-1 rounded text-pink-600 dark:text-pink-400 border border-pink-200 dark:border-pink-800">
                          {landStats.completed}/{landStats.total}
                        </span>
                      </div>
                      <div>
                        {filteredAttractions.map((ride) => renderItem(ride, 'text-pink-500'))}
                        {showWishlistOnly && filteredAttractions.length < land.attractions.length && filteredAttractions.length > 0 && (
                          <div className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500 italic">
                            {land.attractions.length - filteredAttractions.length} more hidden by wishlist filter
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {showWishlistOnly && PARKS_DATA.disneylandPark.lands.every(land => land.attractions.filter(shouldShowItem).length === 0) && (
                  <div className="text-center py-12 text-gray-400 dark:text-gray-500">No wishlisted items in Disneyland Park</div>
                )}
              </div>
            )}

            {/* --- WALT DISNEY STUDIOS TAB --- */}
            {activeTab === 'park2' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-blue-100 dark:border-blue-900/40">
                  <h2 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-1">Adventure World Rides</h2>
                  <ProgressBar current={p2Stats.completed} total={p2Stats.total} colorClass="bg-blue-500" />
                </div>

                {PARKS_DATA.wds.lands.map((land) => {
                  const filteredAttractions = land.attractions.filter(shouldShowItem);
                  if (showWishlistOnly && filteredAttractions.length === 0) return null;
                  const landStats = getProgress(land.attractions);
                  const isFullyComplete = landStats.completed === landStats.total && landStats.total > 0;

                  return (
                    <div key={land.name} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border-l-4 ${isFullyComplete ? 'border-green-400' : 'border-blue-500'}`}>
                      <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3 flex justify-between items-center border-b border-blue-100 dark:border-blue-900/40">
                        <h3 className="font-bold text-blue-900 dark:text-blue-300">{land.name}</h3>
                        <span className="text-xs font-mono bg-white dark:bg-gray-700 px-2 py-1 rounded text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                          {landStats.completed}/{landStats.total}
                        </span>
                      </div>
                      <div>
                        {filteredAttractions.map((ride) => renderItem(ride, 'text-blue-500'))}
                        {showWishlistOnly && filteredAttractions.length < land.attractions.length && filteredAttractions.length > 0 && (
                          <div className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500 italic">
                            {land.attractions.length - filteredAttractions.length} more hidden by wishlist filter
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {showWishlistOnly && PARKS_DATA.wds.lands.every(land => land.attractions.filter(shouldShowItem).length === 0) && (
                  <div className="text-center py-12 text-gray-400 dark:text-gray-500">No wishlisted items in Adventure World</div>
                )}
              </div>
            )}

            {/* --- ENTERTAINMENT TAB --- */}
            {activeTab === 'shows' && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-purple-100 dark:border-purple-900/40">
                  <h2 className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-1">Entertainment Tracker</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Parades, Shows & Character Meets</p>
                  <ProgressBar current={entStats.completed} total={entStats.total} colorClass="bg-purple-500" />
                </div>

                {/* Disneyland Park Entertainment */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 px-2">
                    <Castle className="text-pink-500" size={20} /> Disneyland Park
                  </h3>
                  {ENTERTAINMENT_DATA.disneylandPark.sections.map((section) => {
                    const filteredItems = section.items.filter(shouldShowItem);
                    if (showWishlistOnly && filteredItems.length === 0) return null;

                    return (
                      <div key={section.title} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border-l-4 border-purple-400">
                        <div className="bg-purple-50 dark:bg-purple-900/20 px-4 py-3 flex items-center gap-2 border-b border-purple-100 dark:border-purple-900/40 text-purple-900 dark:text-purple-300 font-bold">
                          {section.title === 'Parades & Nighttime Spectaculars' && <Star size={18} />}
                          {section.title === 'Stage Shows' && <Music size={18} />}
                          {section.title === 'Character Meets' && <Users size={18} />}
                          {section.title}
                        </div>
                        <div>
                          {filteredItems.map((item) => renderItem(item, 'text-purple-500'))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Adventure World Entertainment */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 px-2 mt-8">
                    <Rocket className="text-blue-500" size={20} /> Disney Adventure World
                  </h3>
                  {ENTERTAINMENT_DATA.wds.sections.map((section) => {
                    const filteredItems = section.items.filter(shouldShowItem);
                    if (showWishlistOnly && filteredItems.length === 0) return null;

                    return (
                      <div key={section.title} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border-l-4 border-indigo-400">
                        <div className="bg-indigo-50 dark:bg-indigo-900/20 px-4 py-3 flex items-center gap-2 border-b border-indigo-100 dark:border-indigo-900/40 text-indigo-900 dark:text-indigo-300 font-bold">
                          {section.title === 'Parades & Nighttime Spectaculars' && <Star size={18} />}
                          {section.title === 'Stage Shows' && <Music size={18} />}
                          {section.title === 'Character Meets' && <Users size={18} />}
                          {section.title}
                        </div>
                        <div>
                          {filteredItems.map((item) => renderItem(item, 'text-indigo-500'))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* --- HOTELS TAB --- */}
            {activeTab === 'hotels' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-amber-100 dark:border-amber-900/40">
                  <h2 className="text-xl font-bold text-amber-600 dark:text-amber-400 mb-1">Disney Hotels</h2>
                  <ProgressBar current={hotelStats.completed} total={hotelStats.total} colorClass="bg-amber-500" />
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border-l-4 border-amber-500">
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {HOTELS_DATA.map((hotel) => {
                      if (showWishlistOnly && !getWishlistStatus(hotel.name)) return null;
                      return renderItem(
                        hotel.name,
                        'text-amber-500',
                        <div className="flex items-center mt-1 gap-3">
                          <div className="flex text-yellow-400">
                            {[...Array(hotel.stars)].map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
                          </div>
                          <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">{hotel.theme}</span>
                        </div>
                      );
                    })}
                    {showWishlistOnly && HOTELS_DATA.every(h => !getWishlistStatus(h.name)) && (
                      <div className="text-center py-12 text-gray-400 dark:text-gray-500">No wishlisted hotels</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* --- BOOK YOUR TRIP TAB --- */}
            {activeTab === 'book' && (
              <Suspense fallback={<TabLoadingFallback />}>
                <BookTrip />
              </Suspense>
            )}

            {/* --- STATS TAB --- */}
            {activeTab === 'stats' && (
              <Suspense fallback={<TabLoadingFallback />}>
                <Leaderboard />
              </Suspense>
            )}
          </>
        )}

      </main>
    </div>
  );
}

// Crash fallback shown when Sentry catches an unhandled error
function CrashFallback({ error, resetError }) {
  const isChunkError = error?.message?.includes('Importing a module script failed') ||
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('error loading dynamically imported module');

  useEffect(() => {
    if (isChunkError) {
      // Stale chunk after deploy — hard reload to get fresh HTML + new chunk URLs.
      // Guard against infinite reload loops: only attempt once per session.
      const reloadAttempted = sessionStorage.getItem('chunk-reload-attempted');
      if (!reloadAttempted) {
        sessionStorage.setItem('chunk-reload-attempted', '1');
        window.location.reload();
      }
    }
  }, [isChunkError]);

  if (isChunkError) return null; // Will reload momentarily

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
      <div className="max-w-sm text-center">
        <div className="text-6xl mb-4">🏰</div>
        <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">Something went wrong</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          The app hit an unexpected error. This has been reported automatically and we&apos;ll fix it soon.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={resetError}
            className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Reload App
          </button>
        </div>
        {import.meta.env.DEV && error && (
          <pre className="mt-6 text-left text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg overflow-auto max-h-40">
            {error.toString()}
          </pre>
        )}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <SentryErrorBoundary fallback={({ error, resetError }) => <CrashFallback error={error} resetError={resetError} />}>
      <ThemeProvider>
        <WaitTimesProvider>
          <NotificationAlertsProvider>
            <AuthProvider>
              <FamilyProvider>
                <TrackerApp />
              </FamilyProvider>
            </AuthProvider>
          </NotificationAlertsProvider>
        </WaitTimesProvider>
      </ThemeProvider>
    </SentryErrorBoundary>
  );
}
